import React, { Component } from "react";
import {
  message,
  PageHeader,
  Spin,
  Row,
  Col,
  Select,
  Tag,
  Alert,
  Empty,
} from "antd";
import {
  dineroDisabledMask,
  Form,
  FormItem,
  InputNumberItem,
  SelectItem,
  DatePickerItem,
  RangePickerItem,
  InputItem,
  ButtonItem,
  NotificacionPolizas,
  ErrorItem,
} from "../../../../Components/Items";
import round2Decimals from "../../../../Components/round2Decimals";
import DynamicCardForm from "../../../../Components/DynamicCardForm";
import TabbedDynamicTable from "../../../../Components/TabbedDynamicTable";
import {
  CalculatorOutlined,
  FilterOutlined,
  SearchOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import { connect } from "react-redux";

const { Option } = Select;
moment.locale("es");

const MXN_ID = "60c7e4396465ad440c41c15a";
const FORMA_DE_PAGO_EFECTIVO_ID = "6125149afe07662acc100d5a";

/**
 * Pago en bloque de facturas de Cuentas por Pagar contra una caja chica.
 *
 * La pantalla de CxP (VerFacturasProveedor) ya paga varias facturas y ya acepta una caja
 * chica en su selector de bancos, pero solo de UN proveedor por vuelta. Aqui la lista mezcla
 * proveedores y el backend se encarga de partir el pago por proveedor.
 *
 * A diferencia de ReponerCajaChica -de donde sale casi toda la forma de esta pantalla- el
 * filtrado se hace en el SERVIDOR: alla la lista son las facturas pendientes de una sola
 * caja, aqui es CxP entero y montar miles de renglones en el Form.List para esconderlos con
 * CSS no se sostiene.
 *
 * El costo de filtrar en el servidor es que cada busqueda vuelve a armar el Form.List. Por
 * eso las cantidades capturadas se guardan aparte, indexadas por id de factura, y se
 * reinyectan despues de cada busqueda: cambiar un filtro no debe borrar lo ya capturado.
 */
class PagarCxpCajaChica extends Component {
  formRef = React.createRef();

  //Fuera del state a proposito: no se dibuja con esto, solo sobrevive a los re-fetch.
  cantidadesPorFactura = {};

  state = {
    loading: false,
    buscando: false,
    facturas: [],
    busquedaHecha: false,
    objetoProveedores: null,
    objetoFormasDePago: null,
    objetoProyectos: null,
    id: this.props.location.state.id,
    nombreCaja: this.props.location.state.nombre,
    monedaIdCaja: MXN_ID,
    monedaAbreviacionCaja: "MXN",
    //Filtros de la busqueda. Viven aqui y NO en el Form porque `onFinish` manda
    //`{...values}` al backend: un FormItem de filtro viajaria en el body del pago.
    filtroProveedores: [],
    filtroFormasDePago: [],
    filtroMetodosPago: [],
    filtroFechas: null,
    resultados: null,
  };

  componentDidMount() {
    this.consultarTodo();
  }

  async consultarTodo() {
    this.setState({ loading: true });
    await axios
      .post(
        "proveedores/selectProveedores",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.status !== 404) {
          const proveedores = res.data?.proveedores || res.data || [];
          this.setState({
            objetoProveedores: proveedores.map((value) => (
              <Option value={value._id} key={value._id}>
                {value.persona
                  ? value.razonSocial
                  : `${value.nombre || ""} ${value.apellidoPaterno || ""} ${
                      value.apellidoMaterno || ""
                    }`.trim()}
              </Option>
            )),
          });
        }
      });
    await axios
      .post(
        "formasdepago/selectFormasDePago",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.status !== 404) {
          this.setState({
            objetoFormasDePago: res.data.map((value) => (
              <Option value={value._id} key={value._id}>
                {value?.c_FormaPago + " - " + value.descripcion}
              </Option>
            )),
          });
        }
      });
    await axios
      .post(
        "proyectos/selectProyectos",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.status !== 404) {
          this.setState({
            objetoProyectos: res.data.map((value) => (
              <Option value={value._id} key={value._id}>
                {value.nombre}
              </Option>
            )),
          });
        }
      });
    //La moneda de la caja no es cosmetica: el backend rechaza el pago si la moneda del pago
    //no es la del banco, asi que es la que manda en toda la aritmetica de tipos de cambio.
    await axios
      .post(
        "cajaefectivo/selectCajaEfectivo",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.status !== 404) {
          const cajaActual = res.data.find(
            (value) => value._id.toString() === this.state.id.toString()
          );
          if (cajaActual) {
            this.setState({
              monedaIdCaja: cajaActual.monedaId
                ? cajaActual.monedaId.toString()
                : MXN_ID,
              monedaAbreviacionCaja: cajaActual.moneda?.abreviacion || "MXN",
            });
          }
        }
      });
    this.setState({ loading: false });
    await this.buscarFacturas();
  }

  //Se llama ANTES de cada busqueda: lo capturado se indexa por id de factura para poder
  //devolverlo a su renglon aunque la siguiente busqueda traiga otro orden u otras facturas.
  guardarCantidadesCapturadas() {
    const form = this.formRef.current;
    if (!form) return;
    const renglones = form.getFieldValue("facturas") || [];
    renglones.forEach((renglon, arrayKey) => {
      const factura = this.state.facturas[arrayKey];
      if (!factura) return;
      const id = factura._id.toString();
      if (renglon?.cantidadPagar > 0) {
        this.cantidadesPorFactura[id] = {
          cantidadPagar: renglon.cantidadPagar,
          tipoDeCambioCompra: renglon.tipoDeCambioCompra,
        };
      } else {
        delete this.cantidadesPorFactura[id];
      }
    });
  }

  //Ojo: aqui NO se limpia `resultados`. Despues de pagar se vuelve a buscar para refrescar
  //la lista, y limpiarlo aqui borraria el aviso de los proveedores que fallaron justo antes
  //de que alguien alcance a leerlo. Se limpia al iniciar un pago o una busqueda manual.
  async buscarFacturas() {
    this.guardarCantidadesCapturadas();
    this.setState({ buscando: true });
    const [desde, hasta] = this.state.filtroFechas || [];
    await axios
      .post(
        "cajaefectivo/selectFacturasPorPagarCXP",
        {
          empresaId: this.props.empresaId,
          proveedoresIds: this.state.filtroProveedores,
          formasDePagoIds: this.state.filtroFormasDePago,
          metodosPago: this.state.filtroMetodosPago,
          fechaFacturaDesde: desde ? desde.toISOString() : undefined,
          fechaFacturaHasta: hasta ? hasta.toISOString() : undefined,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          const facturas = res.data.facturas || [];
          this.setState({ facturas, busquedaHecha: true });
          this.formRef.current.setFieldsValue({
            facturas: facturas.map((factura) => {
              const capturado = this.cantidadesPorFactura[factura._id.toString()];
              return {
                cantidadPagar: capturado ? capturado.cantidadPagar : null,
                tipoDeCambioCompra:
                  capturado?.tipoDeCambioCompra || factura.tipoDeCambio,
              };
            }),
          });
        } else {
          message.error("Hubo un error y no se pudieron consultar las facturas");
        }
      })
      .catch(() => {
        message.error("Hubo un error y no se pudieron consultar las facturas");
      });
    this.setState({ buscando: false });
  }

  limpiarFiltros() {
    this.setState(
      {
        filtroProveedores: [],
        filtroFormasDePago: [],
        filtroMetodosPago: [],
        filtroFechas: null,
        resultados: null,
      },
      () => this.buscarFacturas()
    );
  }

  hayFiltroActivo() {
    const {
      filtroProveedores,
      filtroFormasDePago,
      filtroMetodosPago,
      filtroFechas,
    } = this.state;
    return (
      filtroProveedores.length > 0 ||
      filtroFormasDePago.length > 0 ||
      filtroMetodosPago.length > 0 ||
      !!filtroFechas
    );
  }

  textoMetodoPago(metodoPago) {
    if (metodoPago === 1) return "PUE";
    if (metodoPago === 2) return "PPD";
    return "";
  }

  /**
   * Tipo de cambio con el que se convierte un renglon.
   *
   * El respaldo con `factura.tipoDeCambio` no es cosmetico: cuando la caja es MXN la columna
   * de T.C. no se dibuja, asi que el renglon puede no tener el campo montado. Cayendo a 1 en
   * ese caso, una factura en dolares tomaria su saldo como si fueran pesos.
   */
  tipoDeCambioDeRenglon(factura, tipoDeCambioCompra) {
    return tipoDeCambioCompra || factura.tipoDeCambio || 1;
  }

  //Saldo de la factura expresado en la moneda de la caja: es el tope real del renglon.
  saldoEnMonedaDelPago(factura, tipoDeCambioCompra, tipoDeCambioPago) {
    const equivalencia =
      (tipoDeCambioPago || 1) / this.tipoDeCambioDeRenglon(factura, tipoDeCambioCompra);
    if (!equivalencia) return 0;
    return round2Decimals(factura.saldoPendiente / equivalencia);
  }

  setMaxPagarTodos = () => {
    const form = this.formRef.current;
    if (!form) return;
    const renglones = form.getFieldValue("facturas") || [];
    const tipoDeCambioPago = form.getFieldValue("tipoDeCambioPago") || 1;
    form.setFieldsValue({
      facturas: renglones.map((renglon, arrayKey) => {
        const factura = this.state.facturas[arrayKey];
        if (!factura) return renglon;
        const maximo = this.saldoEnMonedaDelPago(
          factura,
          renglon?.tipoDeCambioCompra,
          tipoDeCambioPago
        );
        return { ...renglon, cantidadPagar: maximo > 0 ? maximo : null };
      }),
    });
  };

  onFinishFailed(e) {
    ErrorItem(e);
  }

  onFinish(values) {
    const tipoDeCambioPago = values.tipoDeCambioPago || 1;
    const renglones = values.facturas || [];
    const facturasAPagar = [];

    for (let arrayKey = 0; arrayKey < renglones.length; arrayKey++) {
      const renglon = renglones[arrayKey];
      const factura = this.state.facturas[arrayKey];
      if (!factura) continue;
      const cantidadPagar = renglon?.cantidadPagar || 0;
      if (cantidadPagar <= 0) continue;

      const tipoDeCambioCompra = this.tipoDeCambioDeRenglon(
        factura,
        renglon.tipoDeCambioCompra
      );
      if (factura.monedaId?.toString() !== MXN_ID && tipoDeCambioCompra <= 0) {
        return message.error(
          `Factura ${factura.codigoFactura}: el tipo de cambio debe ser mayor a 0`
        );
      }

      //Cuando la factura ya esta en la moneda del pago no hay conversion que valga: si los
      //dos tipos de cambio no coinciden, el monto que se aplicaria no seria el capturado.
      if (
        factura.monedaId?.toString() === this.state.monedaIdCaja &&
        round2Decimals(tipoDeCambioCompra) !== round2Decimals(tipoDeCambioPago)
      ) {
        return message.error(
          `Factura ${factura.codigoFactura}: cuando la moneda de la factura y la de la caja son iguales, el tipo de cambio debe ser el mismo`
        );
      }

      const maximo = this.saldoEnMonedaDelPago(
        factura,
        tipoDeCambioCompra,
        tipoDeCambioPago
      );
      if (round2Decimals(cantidadPagar) > maximo + 0.01) {
        return message.error(
          `Factura ${factura.codigoFactura}: la cantidad a pagar (${dineroDisabledMask(
            cantidadPagar
          )}) excede el saldo pendiente (${dineroDisabledMask(maximo)})`
        );
      }

      facturasAPagar.push({
        _id: factura._id,
        proveedorId: factura.proveedorId,
        cantidadPagar,
        tipoDeCambioCompra,
      });
    }

    if (facturasAPagar.length === 0)
      return message.info("Se necesitan cantidades para poder hacer esta operación");

    this.setState({ loading: true, resultados: null });
    axios
      .post(
        "cajaefectivo/pagarFacturasCXPCajaChica",
        {
          empresaId: this.props.empresaId,
          cajaId: this.state.id,
          proyectoId: values.proyectoId,
          fechaPago: values.fechaPago,
          formaDePagoId: values.formaDePagoId,
          referencia: values.referencia,
          tipoDeCambioPago,
          facturas: facturasAPagar,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success === 2) {
          const conError = (res.data.resultados || []).filter(
            (resultado) => resultado.success !== 2
          );
          if (conError.length > 0) {
            //Hubo pagos que si se hicieron: decir "listo" a secas escondería los que no.
            message.warning(
              "Se realizaron los pagos, pero " +
                conError.length +
                (conError.length === 1
                  ? " proveedor fallo"
                  : " proveedores fallaron")
            );
          } else {
            message.success("Se han realizado los pagos");
          }
          NotificacionPolizas(res.data.polizasId);
          this.setState({ resultados: res.data.resultados || null });
          this.cantidadesPorFactura = {};
          this.formRef.current.resetFields();
          this.buscarFacturas();
        } else if (res.data.success === 1) {
          this.setState({ resultados: res.data.resultados || null });
          message.error(res.data.message || "No se pudieron realizar los pagos");
        } else {
          message.error("Hubo un error y no se realizaron los pagos");
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error("Hubo un error y no se realizaron los pagos");
      });
  }

  atras() {
    this.props.history.goBack();
  }

  getFacturasColumns() {
    const esMxn = this.state.monedaIdCaja === MXN_ID;
    return [
      { title: "Código", width: "9%" },
      { title: "Fecha", width: "7%" },
      { title: "Proveedor", width: "16%" },
      { title: "Proyecto", width: "10%" },
      { title: "Forma de Pago", width: "11%" },
      { title: "Método", width: "5%" },
      ...(esMxn ? [] : [{ title: "T.C.", width: "7%" }]),
      { title: "Total a Pagar", width: "9%" },
      { title: "Total Pagado", width: "9%" },
      { title: "Saldo Pendiente", width: "9%" },
      {
        title: (
          <>
            Cantidad a Pagar
            <CalculatorOutlined
              onClick={this.setMaxPagarTodos}
              title="Poner máximos"
              style={{ marginLeft: 5, color: "green", cursor: "pointer" }}
            />
          </>
        ),
        width: "12%",
      },
    ];
  }

  renderFiltros() {
    return (
      <DynamicCardForm
        title={
          <span>
            <FilterOutlined style={{ color: "#00BFD6", marginRight: 6 }} />
            FILTROS
          </span>
        }
        span={24}
        xs={24}
        sm={24}
        md={24}
        lg={24}
        xl={24}
      >
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <SelectItem
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              style={{ width: "100%" }}
              placeholder="Proveedor"
              value={this.state.filtroProveedores}
              onChange={(value) =>
                this.setState({ filtroProveedores: value || [] })
              }
            >
              {this.state.objetoProveedores}
            </SelectItem>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <RangePickerItem
              style={{ width: "100%" }}
              placeholder={["Factura desde", "Factura hasta"]}
              value={this.state.filtroFechas}
              onChange={(value) => this.setState({ filtroFechas: value })}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <SelectItem
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              style={{ width: "100%" }}
              placeholder="Forma de Pago"
              value={this.state.filtroFormasDePago}
              onChange={(value) =>
                this.setState({ filtroFormasDePago: value || [] })
              }
            >
              {this.state.objetoFormasDePago}
            </SelectItem>
          </Col>
          <Col xs={24} sm={12} md={8} lg={3} xl={3}>
            <SelectItem
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              style={{ width: "100%" }}
              placeholder="Método"
              value={this.state.filtroMetodosPago}
              onChange={(value) =>
                this.setState({ filtroMetodosPago: value || [] })
              }
            >
              <Option value={1} key="1">
                PUE - Pago en una sola exhibición
              </Option>
              <Option value={2} key="2">
                PPD - Pago en parcialidades o diferido
              </Option>
            </SelectItem>
          </Col>
          <Col xs={24} sm={24} md={24} lg={3} xl={3}>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <ButtonItem
                type="primary"
                cyanB
                icon={<SearchOutlined />}
                onClick={() =>
                  this.setState({ resultados: null }, () => this.buscarFacturas())
                }
              >
                Buscar
              </ButtonItem>
              {this.hayFiltroActivo() && (
                <ButtonItem onClick={() => this.limpiarFiltros()}>
                  Limpiar
                </ButtonItem>
              )}
            </div>
          </Col>
        </Row>
      </DynamicCardForm>
    );
  }

  renderResumenTabla() {
    const totalSaldo = this.state.facturas.reduce(
      (acumulado, factura) =>
        acumulado + (factura.saldoPendiente > 0 ? factura.saldoPendiente : 0),
      0
    );
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 6,
          marginRight: "auto",
        }}
      >
        <span style={{ fontSize: 12, color: "#555", whiteSpace: "nowrap" }}>
          {this.state.facturas.length +
            (this.state.facturas.length === 1 ? " factura" : " facturas")}
        </span>
        <Tag
          color="#00c1d61b"
          style={{
            fontSize: 12,
            border: "1px solid #00BFD6",
            borderRadius: 6,
            fontWeight: 600,
            color: "#026b77",
            marginRight: 0,
          }}
        >
          {"Saldo pendiente: " + dineroDisabledMask(round2Decimals(totalSaldo))}
        </Tag>
      </div>
    );
  }

  renderFacturaRow({ key, name, fieldKey, restField, arrayKey }) {
    const factura = this.state.facturas[arrayKey];
    if (!factura) return null;
    const esMxn = this.state.monedaIdCaja === MXN_ID;
    const facturaEsMxn = factura.monedaId?.toString() === MXN_ID;

    return (
      <tr key={key}>
        <td className="td-border-center">
          <p style={{ marginBottom: 0, fontWeight: 600, color: "#1a3a4a" }}>
            {factura.codigoFactura}
          </p>
        </td>
        <td className="td-border-center">
          <p style={{ marginBottom: 0, color: "#555" }}>
            {moment(factura.fechaFactura).format("L")}
          </p>
        </td>
        <td className="td-border-center">
          <p style={{ marginBottom: 0, color: "#555" }}>
            {factura.proveedorNombre}
          </p>
        </td>
        <td className="td-border-center">
          <p style={{ marginBottom: 0, color: "#555" }}>
            {factura.proyectoNombre}
          </p>
        </td>
        <td className="td-border-center">
          <p style={{ marginBottom: 0, color: "#555" }}>
            {factura.formaDePagoTexto}
          </p>
        </td>
        <td className="td-border-center">
          <p style={{ marginBottom: 0, color: "#555" }}>
            {this.textoMetodoPago(factura.metodoPago)}
          </p>
        </td>
        {!esMxn && (
          <td className="td-border">
            <FormItem
              {...restField}
              name={[name, "tipoDeCambioCompra"]}
              fieldKey={[arrayKey, "tipoDeCambioCompra"]}
              noStyle
              type="number"
            >
              <InputNumberItem
                style={{ width: "100%" }}
                disabled={facturaEsMxn}
                size="small"
              />
            </FormItem>
          </td>
        )}
        <td className="td-border-center">
          <p style={{ marginBottom: 0 }}>
            {dineroDisabledMask(factura.totalPagar) +
              " " +
              factura.monedaAbreviacion}
          </p>
        </td>
        <td className="td-border-center">
          <p style={{ marginBottom: 0 }}>
            {dineroDisabledMask(factura.totalPagado)}
          </p>
        </td>
        <td className="td-border-center">
          <p style={{ marginBottom: 0, color: "#ee3b3e", fontWeight: 600 }}>
            {dineroDisabledMask(factura.saldoPendiente)}
          </p>
        </td>
        <td className="td-border">
          <FormItem noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const renglones = getFieldValue("facturas") || [];
              const maximo = this.saldoEnMonedaDelPago(
                factura,
                renglones?.[arrayKey]?.tipoDeCambioCompra,
                getFieldValue("tipoDeCambioPago") || 1
              );
              const cantidadPagar = renglones?.[arrayKey]?.cantidadPagar || 0;
              const esTotal = cantidadPagar > 0 && cantidadPagar === maximo;
              return (
                <div
                  style={{ display: "flex", alignItems: "center", gap: 4, width: "100%" }}
                >
                  <ButtonItem
                    size="small"
                    olivo
                    style={{ padding: "12px 12px" }}
                    type={esTotal ? "primary" : "default"}
                    title="Asignar total"
                    onClick={() => {
                      const facturasVal =
                        this.formRef.current.getFieldValue("facturas");
                      facturasVal[arrayKey].cantidadPagar = esTotal ? null : maximo;
                      this.formRef.current.setFieldsValue({
                        facturas: facturasVal,
                      });
                    }}
                  >
                    Total
                  </ButtonItem>
                  <div
                    style={{ flex: 1 }}
                    className={esTotal ? "cantidad-pagar-total" : ""}
                  >
                    <FormItem
                      {...restField}
                      name={[name, "cantidadPagar"]}
                      fieldKey={[arrayKey, "cantidadPagar"]}
                      noStyle
                      numberCero
                      type="number"
                      max={maximo}
                    >
                      <InputNumberItem
                        style={{ width: "100%" }}
                        placeholder={"Max: " + maximo}
                        dinero
                        size="small"
                      />
                    </FormItem>
                  </div>
                </div>
              );
            }}
          </FormItem>
        </td>
      </tr>
    );
  }

  renderResultados() {
    if (!this.state.resultados) return null;
    const conError = this.state.resultados.filter(
      (resultado) => resultado.success !== 2
    );
    if (conError.length === 0) return null;
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Hubo proveedores cuyas facturas no se pudieron pagar"
        description={
          <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
            {conError.map((resultado) => (
              <li key={resultado.proveedorId}>
                {(resultado.proveedorNombre || resultado.proveedorId) +
                  ": " +
                  resultado.message}
              </li>
            ))}
          </ul>
        }
      />
    );
  }

  render() {
    const esMxn = this.state.monedaIdCaja === MXN_ID;

    return (
      <>
        <style>{`
          .cantidad-pagar-total .ant-input-number-input {
            font-weight: 700 !important;
          }
        `}</style>
        <PageHeader
          onBack={this.atras.bind(this)}
          title={"Pagar Facturas de CxP : " + this.state.nombreCaja}
          extra={[
            <Tag
              key="monedaCaja"
              color="#00c1d61b"
              style={{
                fontSize: 13,
                border: "1px solid #00BFD6",
                padding: "4px 10px",
                borderRadius: 6,
                fontWeight: 600,
                color: "#026b77",
              }}
            >
              <WalletOutlined style={{ marginRight: 6 }} />
              {"Moneda: " + this.state.monedaAbreviacionCaja}
            </Tag>,
          ]}
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 10,
          }}
        />
        <div className="card-background">
          <Spin spinning={this.state.loading || this.state.buscando}>
            <Form
              ref={this.formRef}
              name="pagarCxpCajaChica"
              layout
              onFinish={this.onFinish.bind(this)}
              onFinishFailed={this.onFinishFailed}
            >
              {this.renderResultados()}

              <Row gutter={[16, 16]}>
                <DynamicCardForm
                  title={<span>PAGO</span>}
                  span={24}
                  xs={24}
                  sm={24}
                  md={24}
                  lg={24}
                  xl={24}
                >
                  <Row gutter={16}>
                    <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                      <FormItem
                        label="Fecha de Pago"
                        name="fechaPago"
                        required
                        margin
                        labelCol={{ span: 9 }}
                        wrapperCol={{ span: 15 }}
                        initialValue={moment()}
                      >
                        <DatePickerItem
                          placeholder="Fecha de Pago"
                          allowClear={false}
                        />
                      </FormItem>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                      <FormItem
                        label="Proyecto"
                        name="proyectoId"
                        margin
                        required
                        labelCol={{ span: 9 }}
                        wrapperCol={{ span: 15 }}
                      >
                        <SelectItem placeholder="Proyecto">
                          {this.state.objetoProyectos}
                        </SelectItem>
                      </FormItem>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                      <FormItem
                        label="Forma de Pago"
                        name="formaDePagoId"
                        margin
                        required
                        labelCol={{ span: 9 }}
                        wrapperCol={{ span: 15 }}
                        initialValue={FORMA_DE_PAGO_EFECTIVO_ID}
                      >
                        <SelectItem placeholder="Forma de Pago">
                          {this.state.objetoFormasDePago}
                        </SelectItem>
                      </FormItem>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                      <FormItem
                        name="referencia"
                        label="Referencia"
                        margin
                        pattern
                        labelCol={{ span: 9 }}
                        wrapperCol={{ span: 15 }}
                      >
                        <InputItem placeholder="Referencia" />
                      </FormItem>
                    </Col>
                    {!esMxn && (
                      <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                        <FormItem
                          label="T.C. Pago"
                          name="tipoDeCambioPago"
                          margin
                          required
                          type="number"
                          labelCol={{ span: 9 }}
                          wrapperCol={{ span: 15 }}
                          initialValue={1}
                        >
                          <InputNumberItem placeholder="Tipo de Cambio" />
                        </FormItem>
                      </Col>
                    )}
                  </Row>
                </DynamicCardForm>

                {this.renderFiltros()}
              </Row>

              <div style={{ marginTop: 24, marginBottom: 50 }}>
                {this.state.busquedaHecha && this.state.facturas.length === 0 ? (
                  <Empty
                    description={
                      this.hayFiltroActivo()
                        ? "No hay facturas por pagar con esos filtros"
                        : "No hay facturas por pagar"
                    }
                  />
                ) : (
                  <TabbedDynamicTable
                    tabs={[
                      {
                        key: "facturas",
                        label: "Facturas por Pagar",
                        listName: "facturas",
                        hideTableWhenEmpty: false,
                        columns: this.getFacturasColumns(),
                        renderHeader: () => this.renderResumenTabla(),
                        renderRow: (rowProps) => this.renderFacturaRow(rowProps),
                      },
                    ]}
                  />
                )}
              </div>

              <Row justify="end" style={{ marginTop: 32, marginBottom: 16 }}>
                <Col xs={24} sm={16} md={10} lg={10} xl={8}>
                  <FormItem noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const renglones = getFieldValue("facturas");
                      if (!renglones) return null;
                      let total = 0;
                      let proveedores = new Set();
                      renglones.forEach((renglon, arrayKey) => {
                        const cantidad = renglon?.cantidadPagar || 0;
                        if (cantidad <= 0) return;
                        total += cantidad;
                        const factura = this.state.facturas[arrayKey];
                        if (factura?.proveedorId)
                          proveedores.add(factura.proveedorId.toString());
                      });
                      return (
                        <DynamicCardForm
                          span={24}
                          noMargin
                          bodyStyle={{
                            padding: "12px 16px",
                            borderLeft: "4px solid #FF6A0D",
                            borderTopLeftRadius: "8px",
                            borderBottomLeftRadius: "8px",
                          }}
                        >
                          <table
                            className="table"
                            style={{ width: "100%", margin: 0 }}
                          >
                            <tbody>
                              {proveedores.size > 0 && (
                                <tr>
                                  <th className="th-totales" style={{ fontSize: 14 }}>
                                    Proveedores a pagar:
                                  </th>
                                  <td
                                    className="td-totales-right"
                                    style={{ fontSize: 14 }}
                                  >
                                    {proveedores.size}
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <th
                                  className="th-totales"
                                  style={{ fontSize: 17, fontWeight: 700 }}
                                >
                                  Total a pagar:
                                </th>
                                <td
                                  className="td-totales-right"
                                  style={{ fontSize: 18, fontWeight: 700 }}
                                >
                                  {dineroDisabledMask(round2Decimals(total))}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </DynamicCardForm>
                      );
                    }}
                  </FormItem>
                </Col>
              </Row>

              <div className="general-form-action-buttons-wrapper">
                <div className="general-form-action-buttons">
                  <ButtonItem type="primary" htmlType="submit" cyanB hotkey="alt+P">
                    Pagar
                  </ButtonItem>
                </div>
              </div>
            </Form>
          </Spin>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(PagarCxpCajaChica);
